import React, { useState, useEffect, useRef } from "react";
import { Form, InputGroup } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useCurrency } from "../_helper/currencyContext";

const CurrencyAmountInput1 = ({
    label = "Amount",
    amount,                  // number | null
    onAmountChange,          // (newAmount: number) => void
    onCurrencyChange,        // optional (newCurrency: string) => void
    disabled = false,
    errors = {},
    disableScrollProps = {},
    defaultCurrency,
    originalAmount,
    paidInCurrency,
    settlementData
}) => {
    const {
        currency: defaultCurrencyFromContext,
        allowedCurrencies,
        getCurrencySymbol,
        convertCurrency
    } = useCurrency();

    const { t } = useTranslation();

    const baseCurrency = paidInCurrency || defaultCurrency || defaultCurrencyFromContext;

    const [localCurrency, setLocalCurrency] = useState(baseCurrency);
    const [inputValue, setInputValue] = useState("");

    const prevCurrencyRef = useRef(baseCurrency);
    const originalAmountRef = useRef(originalAmount ?? amount ?? 0);

    useEffect(() => {
        if (originalAmount !== undefined) {
            originalAmountRef.current = originalAmount ?? amount ?? 0;
        }
    }, [originalAmount]);

    useEffect(() => {
        setInputValue(amount != null && !isNaN(amount) ? formatAmount(amount) : "");
    }, [amount]);

    useEffect(() => {
        const previous = prevCurrencyRef.current;
        setLocalCurrency(baseCurrency);

        if (previous !== baseCurrency && originalAmountRef.current != null) {
            onAmountChange(Number(originalAmountRef.current));
        }

        prevCurrencyRef.current = baseCurrency;
    }, [baseCurrency]);

    // ─────────────────────────────────────────────────────────────
    // Helpers
    // ─────────────────────────────────────────────────────────────
    const formatAmount = (num) => {
        if (num == null || isNaN(num)) return "";
        return Number(num).toLocaleString("en-US", {
            minimumFractionDigits: 0,
            maximumFractionDigits: 2
        });
    };

    const parseAmount = (str) => {
        if (str === "" || str === "." || str === "-") return null;
        const cleaned = str.replace(/,/g, "");
        const num = Number(cleaned);
        return isNaN(num) ? null : num;
    };

    const safeRound = (value) => {
        if (value == null || isNaN(value)) return 0;
        return Number(Number(value).toFixed(2));
    };

    // ─────────────────────────────────────────────────────────────
    // NEW: Restrict input to 2 decimal places
    // ─────────────────────────────────────────────────────────────
    const handleInputChange = (e) => {
        let value = e.target.value;

        // Remove commas for processing
        let cleaned = value.replace(/,/g, "");

        // Allow empty, single dot, or negative sign temporarily
        if (cleaned === "" || cleaned === "." || cleaned === "-") {
            setInputValue(value);
            onAmountChange(0);
            return;
        }

        // Prevent multiple decimal points
        if ((cleaned.match(/\./g) || []).length > 1) {
            return; // ignore input
        }

        // If there's a decimal point, check digits after it
        if (cleaned.includes(".")) {
            const [integerPart, decimalPart] = cleaned.split(".");
            if (decimalPart && decimalPart.length > 2) {
                return; // don't allow more than 2 digits after decimal
            }
        }

        // Valid number so far → update UI and parent
        setInputValue(value);

        const parsed = parseAmount(cleaned);
        if (parsed !== null) {
            onAmountChange(parsed);

            if (localCurrency === baseCurrency) {
                originalAmountRef.current = parsed;
            }
        }
    };

    // ─────────────────────────────────────────────────────────────
    // On blur: format nicely and round to 2 decimals
    // ─────────────────────────────────────────────────────────────
    const handleBlur = () => {
        const parsed = parseAmount(inputValue.replace(/,/g, ""));
        const finalAmount = parsed !== null && parsed >= 0 ? safeRound(parsed) : 0;

        onAmountChange(finalAmount);
        setInputValue(formatAmount(finalAmount));

        if (localCurrency === baseCurrency) {
            originalAmountRef.current = finalAmount;
        }
    };

    // ─────────────────────────────────────────────────────────────
    // Currency change
    // ─────────────────────────────────────────────────────────────
    const handleCurrencyChange = (newCurrency) => {
        if (newCurrency === localCurrency) return;

        const currentAmount = amount ?? 0;

        let convertedAmount = convertCurrency(currentAmount, localCurrency, newCurrency);

        if (convertedAmount == null || isNaN(convertedAmount)) {
            convertedAmount = currentAmount;
        }

        if (newCurrency === baseCurrency && originalAmountRef.current != null) {
            convertedAmount = originalAmountRef.current;
        }

        const finalAmount = safeRound(convertedAmount);

        onAmountChange(finalAmount);
        setLocalCurrency(newCurrency);
        prevCurrencyRef.current = newCurrency;
        onCurrencyChange?.(newCurrency);
    };

    // ─────────────────────────────────────────────────────────────
    // Render
    // ─────────────────────────────────────────────────────────────
    return (
        <Form.Group className="mb-3">
            <Form.Label>{label}</Form.Label>

            <InputGroup>
                <Form.Control
                    type="text"
                    inputMode="decimal"
                    value={inputValue}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    disabled={disabled}
                    isInvalid={!!errors.amount}
                    placeholder="0.00"
                    {...disableScrollProps}
                />

                <Form.Select
                    value={localCurrency}
                    style={{ maxWidth: "110px" }}
                    onChange={(e) => handleCurrencyChange(e.target.value)}
                    disabled={disabled}
                >
                    {allowedCurrencies.map((cur) => (
                        <option key={cur} value={cur}>
                            {cur}
                        </option>
                    ))}
                </Form.Select>

                <Form.Control.Feedback type="invalid">
                    {errors.amount}
                </Form.Control.Feedback>
            </InputGroup>

            {localCurrency !== baseCurrency && originalAmountRef.current != null && (
                <Form.Text className="text-muted">
                    {t("Original amount")}: {getCurrencySymbol(baseCurrency)}
                    {formatAmount(originalAmountRef.current)}
                </Form.Text>
            )}
        </Form.Group>
    );
};

export default CurrencyAmountInput1;